/**
 * Security Service
 *
 * API client for identity SSO settings/providers and admin sessions.
 */

import { apiClient } from "@/services/shared/infrastructure";
import { makeService } from "@/services/shared/infrastructure/service-wrapper";
import { getEffectiveOrgId } from "@/services/shared/infrastructure/tenant-state";

export type IdentitySession = {
  id?: string;
  sessionId?: string;
  userId?: string;
  status?: "active" | "expired" | "revoked" | string;
  ipAddress?: string;
  userAgent?: string;
  deviceType?: string;
  createdAt?: string;
  expiresAt?: string;
  lastSeenAt?: string;
};

export type SSOProvider = {
  id?: string;
  ssoProviderId?: string;
  providerType?: "saml" | "oidc" | "oauth2" | string;
  name?: string;
  enabled?: boolean;
  status?: string;
};

export type SSOSettings = {
  orgId?: string;
  ssoEnabled?: boolean;
  enforceSSO?: boolean;
  allowedProviders?: string[];
  defaultProvider?: string | null;
  jitProvisioning?: boolean;
};

export type OrgApiKey = {
  id?: string;
  keyPrefix?: string;
  name?: string;
  status?: "active" | "revoked" | "expired" | string;
  scopes?: string[];
  lastUsedAt?: string;
  expiresAt?: string | null;
  createdAt?: string;
  createdBy?: string;
};

export type CreatedOrgApiKey = OrgApiKey & {
  secret: string;
};

type SessionsListApiResponse = {
  items?: IdentitySession[];
  nextToken?: string;
};

type SSOProvidersListApiResponse = {
  items?: SSOProvider[];
};

const rawSecurityService = {
  async listSessions(
    params?: { cursor?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<{ items: IdentitySession[]; nextToken?: string }> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");

    const response = await apiClient.get<SessionsListApiResponse>(
      `/orgs/${orgId}/identity/sessions`,
      { params, signal },
    );
    const payload = response.data as SessionsListApiResponse | undefined;
    return {
      items: payload?.items || [],
      nextToken: payload?.nextToken,
    };
  },

  async revokeSession(sessionId: string): Promise<void> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");
    if (!sessionId) throw new Error("Session ID is required");

    await apiClient.delete(`/orgs/${orgId}/identity/sessions/${sessionId}`);
  },

  async listSSOProviders(
    params?: { cursor?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<{ items: SSOProvider[]; nextCursor?: string }> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");

    const response = await apiClient.get<SSOProvidersListApiResponse>(
      `/orgs/${orgId}/identity/sso-providers`,
      { params, signal },
    );
    return {
      items: response.data?.items || [],
      nextCursor: response.meta?.pagination?.nextCursor || undefined,
    };
  },

  async getSSOSettings(signal?: AbortSignal): Promise<SSOSettings> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");

    const response = await apiClient.get<SSOSettings>(
      `/orgs/${orgId}/identity/sso-settings`,
      { signal },
    );
    return response.data || {};
  },

  async updateSSOSettings(
    data: Partial<SSOSettings>,
  ): Promise<SSOSettings> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");

    const response = await apiClient.put<SSOSettings>(
      `/orgs/${orgId}/identity/sso-settings`,
      data,
    );
    return response.data || {};
  },

  async listApiKeys(
    params?: { cursor?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<{ items: OrgApiKey[]; nextToken?: string }> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");

    const response = await apiClient.get<{
      items?: OrgApiKey[];
      nextToken?: string;
    }>(`/orgs/${orgId}/identity/api-keys`, { params, signal });
    const payload = response.data as
      | { items?: OrgApiKey[]; nextToken?: string }
      | undefined;
    return {
      items: payload?.items || [],
      nextToken: payload?.nextToken,
    };
  },

  async createApiKey(input: {
    name: string;
    environment?: "live" | "test";
    scopes?: string[];
  }): Promise<CreatedOrgApiKey> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");

    const response = await apiClient.post<CreatedOrgApiKey>(
      `/orgs/${orgId}/identity/api-keys`,
      input,
    );
    const data = response.data as CreatedOrgApiKey | undefined;
    if (!data?.secret) {
      throw new Error("Create API key response missing secret");
    }
    return data;
  },

  async revokeApiKey(apiKeyId: string): Promise<OrgApiKey> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");
    if (!apiKeyId) throw new Error("API key ID is required");

    const response = await apiClient.post<OrgApiKey>(
      `/orgs/${orgId}/identity/api-keys/${apiKeyId}/revoke`,
    );
    return (response.data as OrgApiKey) || { id: apiKeyId, status: "revoked" };
  },
};

export const securityService = makeService(rawSecurityService, "security");
